import { CloseOutlined } from '@ant-design/icons';
import { Modal, Row, Col } from 'antd';
import Title from 'antd/es/typography/Title';
import Buttons from 'components/Global/Buttons';
import GlobalPayPeriod from 'components/Global/PayPeriod';
import SelectDropdown from 'components/Global/SelectDropdown';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { invalidText, toastText } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
import { reportApis } from 'Api/reports';
import { getAccessibleBranchForUser } from 'Api/masters/branch-code';
import { AppDispatch } from 'redux/store';
import { fetchConstantDropdownAction } from '../../../../redux/actions/constantDropdownAction';

interface DropdownOption {
	label: string;
	value: string;
}

type GenerateP13ModalProps = {
	open: boolean;
	cancelGenerateModal: () => void;
	fetchReportDetails: () => void;
};

const GenerateP13Modal = (props: GenerateP13ModalProps) => {
	const { open, cancelGenerateModal, fetchReportDetails } = props;
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch<AppDispatch>();

	const [selectedYear, setSelectedYear] = useState<string>(
		dayjs().startOf('year').toISOString()
	);
	const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>('');
	const [payPeriodError, setPayPeriodError] = useState<boolean>(false);
	const [branchIds, setBranchIds] = useState<string[]>([]);
	const [departmentIds, setDepartmentIds] = useState<string[]>([]);
	const [accessibleBranches, setAccessibleBranches] = useState<
		DropdownOption[]
	>([]);
	const [isBranchesLoading, setIsBranchesLoading] = useState<boolean>(false);

	const dropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data
	);

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			disabled: loading,
			onclick: () => {
				handleCancelModal();
			},
			minWidth: '10rem',
		},
		{
			text: 'Generate',
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: false,
			onclick: handleSubmit,
			minWidth: '10rem',
		},
	];

	const handleCancelModal = () => {
		setSelectedYear(dayjs().startOf('year').toISOString());
		setSelectedPayPeriod('');
		setPayPeriodError(false);
		setBranchIds([]);
		setDepartmentIds([]);
		cancelGenerateModal();
	};

	async function handleSubmit() {
		if (invalidText(selectedPayPeriod)) {
			setPayPeriodError(true);
			return;
		}

		try {
			setLoading(true);
			await reportApis.generateP13Report({
				payPeriodId: selectedPayPeriod,
				branchIds,
				departmentIds,
			});
			toastText('P13 report generated Successfully', 'success');
			fetchReportDetails();
			handleCancelModal();
		} catch (error: any) {
			const errorMessage =
				error?.response?.data?.message ||
				'Something went wrong while generating P13 report';
			toastText(errorMessage, 'error');
		} finally {
			setLoading(false);
		}
	}

	const fetchAccessibleBranches = async () => {
		try {
			setIsBranchesLoading(true);
			const response = await getAccessibleBranchForUser();
			const branches: DropdownOption[] = response?.data?.data ?? [];
			branches.shift();
			setAccessibleBranches(branches);
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Failed to fetch accessible branches';
			toastText(message, 'error');
		} finally {
			setIsBranchesLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			setSelectedYear(dayjs().startOf('year').toISOString());
			setSelectedPayPeriod('');
			setPayPeriodError(false);
			setBranchIds([]);
			setDepartmentIds([]);
		}
	}, [open]);

	useEffect(() => {
		fetchAccessibleBranches();
		dispatch(fetchConstantDropdownAction({}));
	}, []);

	return (
		<Modal
			open={open}
			width={500}
			maskClosable={false}
			closable={false}
			onCancel={handleCancelModal}
			footer={null}
			style={{ position: 'absolute', top: 140, right: 50 }}
		>
			<div className={`${styles['modal']} modal`}>
				<div className={styles['modal-header']}>
					<Title level={4}>P13</Title>
					<div className={styles['close-icon']} onClick={handleCancelModal}>
						<CloseOutlined />
					</div>
				</div>
				<div>
					<Row className={styles['form-container-card']} gutter={50}>
						<GlobalPayPeriod
							isRow={true}
							selectedYear={selectedYear}
							handleSelectedYear={(value) => setSelectedYear(value)}
							selectedPayPeriod={selectedPayPeriod}
							handlePayPeriod={(value) => {
								setSelectedPayPeriod(value);
								setPayPeriodError(false);
							}}
							isYearRequired={true}
							isPayPeriodRequired={true}
							colSpan={24}
							colStyle={{ marginBottom: '1rem' }}
							payPeriodError={payPeriodError}
							rowStyle={{ margin: '0' }}
						/>
						<Col span={24} className="margin-top-10">
							<SelectDropdown
								defaultOption={true}
								defaultLabel="Select Branch"
								placeholder="Select Branch"
								mode="multiple"
								options={accessibleBranches}
								value={branchIds}
								onChange={(value) => {
									const newBranchIds = value as string[];
									setBranchIds(newBranchIds);

									const validDepartments =
										dropdownOptions?.department
											?.filter((item: any) =>
												newBranchIds.includes(item.branchId)
											)
											.map((item: any) => item.value) || [];

									setDepartmentIds((prev) =>
										prev.filter((id) => validDepartments.includes(id))
									);
								}}
								size="large"
								required={false}
								helperText="Branch is required"
								label="Branch"
								isError={false}
								loading={isBranchesLoading}
								disabled={isBranchesLoading}
							/>
						</Col>
						<Col span={24} className="margin-top-10">
							<SelectDropdown
								defaultOption={true}
								defaultLabel="Select Department"
								placeholder="Select Department"
								mode="multiple"
								options={
									branchIds
										? dropdownOptions?.department?.filter(
												(item: any) =>
													branchIds.includes(item.branchId)
										  )
										: []
								}
								value={departmentIds}
								onChange={(value) => {
									setDepartmentIds(value as string[]);
								}}
								size="large"
								required={false}
								helperText="Department is required"
								label="Department"
								disabled={branchIds.length === 0}
								isError={false}
							/>
						</Col>
					</Row>
					<Row className={styles['modal-buttons']}>
						<Buttons buttons={buttons} />
					</Row>
				</div>
			</div>
		</Modal>
	);
};

export default GenerateP13Modal;
